import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { calculateDaysUntilExpired } from '@/common/FinanceCard/utils';
import type { CreditStatusResponse } from '@/srp/Search/hooks/useGetCreditStatus';
import FinancingType from '~/constants/srp/FinancingType';
import { useIsLoggedIn } from '~/context/common/UserContext';
import type { FinanceInfo } from '~/contracts/srp';
import { getFinancingType } from './getFinancingType';

type ContextType = {
  creditStatus: LoadableState<CreditStatusResponse>;
  financeInfo: FinanceInfo;
  isFinanceInitialized: boolean;
  setFinanceInfo: (financeInfo: FinanceInfo | undefined) => void;
  setIsFinanceInitialized: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldRefreshSoftPull: React.Dispatch<React.SetStateAction<boolean>>;
  shouldRefreshSoftPull: boolean;
};

export const defaultContext: ContextType = {
  creditStatus: defaultLoadableState<CreditStatusResponse>({}),
  financeInfo: {
    coApplicantToggleOn: undefined,
    coApplicantEligibility: undefined,
    creditApplicationExpirationUtc: undefined,
    financingType: FinancingType.EstimatedFinancing,
    hasCoApplicant: undefined,
    maxMonthlyPayment: 2000,
    maxTerm: undefined,
    minDownPayment: 0,
    minDownPaymentNoTrade: undefined
  },
  isFinanceInitialized: false,
  setFinanceInfo() {},
  setIsFinanceInitialized() {},
  setShouldRefreshSoftPull() {},
  shouldRefreshSoftPull: false
};

const FinanceContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  financeInfo?: FinanceInfo;
}>;

export function FinanceContextProvider(props: OwnProps) {
  const creditStatus = useLoadableState<CreditStatusResponse>({});
  const isLoggedIn = useIsLoggedIn();
  const [financeInfo, setFinanceInfo] = useState(
    props.financeInfo ?? defaultContext.financeInfo
  );
  const [isFinanceInitialized, setIsFinanceInitialized] = useState(
    !isLoggedIn && Boolean(props.financeInfo)
  );
  const [shouldRefreshSoftPull, setShouldRefreshSoftPull] = useState(false);

  const setFinanceInfoWithDefault = useCallback(
    (nextFinanceInfo: FinanceInfo | undefined) => {
      setFinanceInfo(nextFinanceInfo ?? defaultContext.financeInfo);
    },
    []
  );

  const value = useMemo<ContextType>(
    () => ({
      creditStatus,
      financeInfo,
      isFinanceInitialized,
      setIsFinanceInitialized,
      setFinanceInfo: setFinanceInfoWithDefault,
      setShouldRefreshSoftPull,
      shouldRefreshSoftPull
    }),
    [
      creditStatus,
      financeInfo,
      isFinanceInitialized,
      setIsFinanceInitialized,
      setFinanceInfoWithDefault,
      setShouldRefreshSoftPull,
      shouldRefreshSoftPull
    ]
  );

  return (
    <FinanceContext.Provider value={value}>
      {props.children}
    </FinanceContext.Provider>
  );
}

FinanceContextProvider.displayName = 'FinanceContextProvider';

export function useCreditStatus(): LoadableState<CreditStatusResponse> {
  return useContext(FinanceContext).creditStatus;
}

export function useDaysToExpired(): number {
  return calculateDaysUntilExpired(
    useContext(FinanceContext).financeInfo.creditApplicationExpirationUtc ??
      null
  );
}

export function useFinanceInfo(): FinanceInfo {
  return useContext(FinanceContext).financeInfo ?? defaultContext.financeInfo;
}

export function useFinanceType(): FinancingType | undefined {
  const {
    creditStatus: {
      data: { creditStatus }
    },
    isFinanceInitialized,
    financeInfo: { financingType }
  } = useContext(FinanceContext);

  // Use Search API credit status when returned otherwise use Credit Status API
  return isFinanceInitialized
    ? financingType
    : getFinancingType({
        hasExpiredSoftPull: creditStatus?.hasExpiredSoftPull ?? false,
        hasValidSoftPull: creditStatus?.hasValidSoftPull ?? false
      });
}

export function useHasSoftPull(): boolean {
  return useFinanceType() === FinancingType.UserFinancing;
}

export function useHasEstimatedTerms(): boolean {
  return useFinanceType() === FinancingType.EstimatedFinancing;
}

export function useHasExpiredSoftPull(): boolean {
  return useFinanceType() === FinancingType.Expired;
}

export function useShouldRefreshSoftPull(): boolean {
  return useContext(FinanceContext).shouldRefreshSoftPull;
}

export function useIsFinanceInitialized(): boolean {
  return useContext(FinanceContext).isFinanceInitialized;
}

export default FinanceContext;
